import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  Paper,
  Fab,
  Chip,
  Avatar,
  Rating,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Pets as PetsIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  LocalHospital as HospitalIcon,
  Vaccines as VaccinesIcon,
  MedicalServices as TreatmentIcon,
  MedicalServices as MedicalIcon,
  WhatsApp as WhatsAppIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  Instagram as InstagramIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  ContactPhone as ContactIcon,
  Flight as FlightIcon,
  Medication as MedicationIcon,
  Favorite as FavoriteIcon,
  ExpandLess as ExpandLessIcon,
  Storefront as PetshopIcon
} from '@mui/icons-material';

const DashboardInicio = () => {
  const navigate = useNavigate();
  const [currentTestimonio, setCurrentTestimonio] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Resetear el testimonio actual si cambia el modo
      setCurrentTestimonio(0);
    };

    const handleClickOutside = (event) => {
      // Cerrar menú móvil si se hace click fuera
      if (mobileMenuOpen && !event.target.closest('.mobile-menu')) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const servicios = [
    {
      titulo: 'Consulta médica',
      descripcion: 'Atención veterinaria personalizada para evaluar el estado de salud de tu mascota y detectar enfermedades.',
      icono: <HospitalIcon fontSize="large" sx={{ color: '#c46494' }} />,
      precio: 'Desde $40.000'
    },
    {
      titulo: 'Vacunación',
      descripcion: 'Aplicación de vacunas esenciales para la prevención de enfermedades, siguiendo el esquema de vacunación adecuado según la edad y especie de tu mascota.',
      icono: <VaccinesIcon fontSize="large" sx={{ color: '#c46494' }} />,
      precio: 'Variable'
    },
    {
      titulo: 'Desparasitación',
      descripcion: 'Eliminación y prevención de parásitos internos y externos que pueden afectar la salud de tu mascota. Se recomienda realizarla periódicamente.',
      icono: <MedicalIcon fontSize="large" sx={{ color: '#c46494' }} />,
      precio: 'Variable'
    },
    {
      titulo: 'Toma de exámenes de laboratorio',
      descripcion: 'Recolección de muestras para análisis clínicos que permiten detectar enfermedades, controlar tratamientos y ofrecer un diagnóstico más completo.',
      icono: <TreatmentIcon fontSize="large" sx={{ color: '#c46494' }} />,
      precio: 'Variable'
    },
    {
      titulo: 'Tratamiento ambulatorio',
      descripcion: 'Aplicación de medicamentos, curaciones y seguimientos sin necesidad de hospitalización, permitiendo que tu mascota se recupere en casa bajo control veterinario.',
      icono: <HospitalIcon fontSize="large" sx={{ color: '#c46494' }} />,
      precio: 'Variable'
    },
    {
      titulo: 'Certificado de viajes nacionales',
      descripcion: 'Expedición del certificado médico veterinario requerido para viajar con tu mascota dentro del país, con examen clínico, verificación del esquema de vacunación y desparasitación.',
      icono: <FlightIcon fontSize="large" sx={{ color: '#c46494' }} />,
      precio: 'Variable'
    },
    {
      titulo: 'Eutanasia compasiva',
      descripcion: 'Acompañamiento respetuoso y libre de dolor en el momento de la despedida, con asesoría previa y apoyo a la familia para tomar la decisión en la comodida de su hogar.',
      icono: <FavoriteIcon fontSize="large" sx={{ color: '#c46494' }} />,
      precio: 'Variable'
    },
    {
      titulo: 'Venta de medicamentos veterinarios',
      descripcion: 'Suministro de medicamentos, alimentos y suplementos de uso veterinario, para el cuidado y bienestar de su mascota con asesoria veterinaria.',
      icono: <MedicationIcon fontSize="large" sx={{ color: '#c46494' }} />,
      precio: 'Variable'
    }
  ];

  const renderServicioCard = (servicio) => (
    <Card 
      sx={{ 
        height: '100%', 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.3s',
        '&:hover': { transform: 'translateY(-5px)' },
        mx: 'auto',
        width: '100%',
        maxWidth: { xs: '100%', sm: '95%', md: '100%' }
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Box sx={{ mb: 2 }}>
          {servicio.icono}
        </Box>
        <Typography 
          variant="h6" 
          gutterBottom 
          fontWeight="bold" 
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.1rem' }, 
            minHeight: { xs: '2.5rem', sm: '3rem' }, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            textAlign: 'center',
            mb: 1
          }}
        >
          {servicio.titulo}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            fontSize: { xs: '0.85rem', sm: '0.9rem' }, 
            minHeight: { xs: '4.5rem', sm: '5rem' },
            lineHeight: 1.5,
            flexGrow: 1
          }}
        >
          {servicio.descripcion}
        </Typography>
        <Box sx={{ mt: 'auto', pt: 3, pb: 1 }}>
          <Chip 
            label={servicio.precio} 
            sx={{ 
              backgroundColor: '#c9aebc', 
              color: '#602442',
              fontWeight: 'bold',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              py: 0.5
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );

  const testimonios = [
    {
      nombre: 'Sebastian Polanco',
      testimonio: 'Excelente atención, mis perros se sintieron muy cómodos durante la consulta. La veterinaria fue muy profesional y cariñosa.',
      rating: 5,
      mascota: 'Nacho, Lupe, Isa y Rocky (Pinschers)'
    },
    {
      nombre: 'Carlos Ruiz',
      testimonio: 'Profesional muy capacitada, recomiendo totalmente sus servicios. Salvaron a mi gata cuando más la necesitaba.',
      rating: 5,
      mascota: 'Luna (Gato Persa)'
    },
    {
      nombre: 'Ana López',
      testimonio: 'Salvó la vida de mi mascota, estoy eternamente agradecida. El servicio a domicilio es increíble.',
      rating: 5,
      mascota: 'Zeus (Pastor Alemán)'
    },
    {
      nombre: 'José Martínez',
      testimonio: 'Muy puntual y profesional. Mi perro se sintió cómodo desde el primer momento.',
      rating: 5,
      mascota: 'Pepe (Perro mestizo)'
    },
    {
      nombre: 'Laura Hernández',
      testimonio: 'El mejor servicio veterinario que he recibido. Muy recomendado para cualquier mascota.',
      rating: 5,
      mascota: 'Mia (Gato mestizo)'
    },
    {
      nombre: 'María García',
      testimonio: 'Increíble dedicación y amor por los animales. La doctora salvó a mi cachorro y ahora está completamente sano.',
      rating: 5,
      mascota: 'Rocco (Golden Retriever)'
    }
  ];

  const horarios = [
    { dia: 'Lunes - Viernes', horario: '8:00 AM - 6:00 PM' },
    { dia: 'Sábados', horario: '8:00 AM - 2:00 PM' }
  ];

  const nextTestimonio = () => {
    const cardsPerGroup = isMobile ? 1 : 2;
    const totalGroups = Math.ceil(testimonios.length / cardsPerGroup);
    setCurrentTestimonio((prev) => (prev + 1) % totalGroups);
  };

  const prevTestimonio = () => {
    const cardsPerGroup = isMobile ? 1 : 2;
    const totalGroups = Math.ceil(testimonios.length / cardsPerGroup);
    setCurrentTestimonio((prev) => (prev - 1 + totalGroups) % totalGroups);
  };

  const goToTestimonio = (index) => {
    setCurrentTestimonio(index);
  };

  return (
    <Box>
      {/* Header/Navbar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: scrolled ? 'rgba(252, 220, 244, 0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          boxShadow: scrolled ? '0 2px 10px rgba(0,0,0,0.1)' : 'none',
          zIndex: 1100,
          transition: 'all 0.3s ease'
        }}
      >
        <Toolbar sx={{ py: { xs: 0.5, sm: 1 }, px: { xs: 1, sm: 2 } }}>
          <Box 
            sx={{ 
              mr: { xs: 1, sm: 2, md: 4 }, 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              flexShrink: 0
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src="/logo.png" 
              alt="Belkysvet Logo" 
              style={{ 
                height: window.innerWidth < 600 ? '40px' : window.innerWidth < 900 ? '50px' : '60px', 
                width: 'auto',
                maxWidth: window.innerWidth < 600 ? '100px' : '200px',
                marginRight: window.innerWidth < 600 ? '4px' : '8px'
              }} 
            />
            <Typography 
              variant="h5" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { xs: '0.8rem', sm: '1rem', md: '1.2rem' },
                textShadow: scrolled ? 'none' : '2px 2px 4px rgba(0,0,0,0.5)',
                transition: 'all 0.3s ease',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              belkysveterinaria
            </Typography>
          </Box>
          
          {/* Spacer para empujar los botones a la derecha en móvil */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Menú de navegación para desktop */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: { md: 2, lg: 4 },
            mr: 2
          }}>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.7)',
                '&:hover': { backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.1)' },
                transition: 'all 0.3s ease',
                px: { md: 1, lg: 2 }
              }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Inicio
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.7)',
                '&:hover': { backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.1)' },
                transition: 'all 0.3s ease',
                px: { md: 1, lg: 2 }
              }}
              onClick={() => {
                navigate('/servicios');
                window.scrollTo(0, 0);
              }}
            >
              Servicios
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.7)',
                '&:hover': { backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.1)' },
                transition: 'all 0.3s ease',
                px: { md: 1, lg: 2 }
              }}
              onClick={() => {
                navigate('/nosotros');
                window.scrollTo(0, 0);
              }}
            >
              Nosotros
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.7)',
                '&:hover': { backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.1)' },
                transition: 'all 0.3s ease',
                px: { md: 1, lg: 2 }
              }}
              onClick={() => {
                navigate('/petshop');
                window.scrollTo(0, 0);
              }}
            >
              Petshop
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: scrolled ? '#602442' : 'white', 
                fontWeight: 'bold',
                fontSize: { md: '1rem', lg: '1.1rem' },
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.7)',
                '&:hover': { backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.1)' },
                transition: 'all 0.3s ease',
                px: { md: 1, lg: 2 }
              }}
              onClick={() => {
                navigate('/contactos');
                window.scrollTo(0, 0);
              }}
            >
              Contacto
            </Button>
          </Box>
          
          {/* Botones para móvil - Solo Menú */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            {/* Menú hamburguesa para móvil */}
            <IconButton
              sx={{ 
                color: scrolled ? '#602442' : 'white',
                padding: '6px'
              }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <ExpandLessIcon /> : <MenuIcon />}
            </IconButton>
          </Box>

        </Toolbar>

        {/* Menú desplegable para móvil */}
        <Collapse in={mobileMenuOpen}>
          <Box 
            className="mobile-menu"
            sx={{ 
              backgroundColor: scrolled ? 'rgba(252, 220, 244, 0.98)' : 'rgba(96, 36, 66, 0.95)',
              backdropFilter: 'blur(10px)',
              borderTop: '1px solid rgba(255, 255, 255, 0.2)',
              display: { xs: 'block', md: 'none' },
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}>
            <Box sx={{ py: 2, px: 3 }}>
              <Button
                fullWidth
                sx={{
                  justifyContent: 'flex-start',
                  color: scrolled ? '#602442' : 'white',
                  fontWeight: 'bold',
                  py: 1.5,
                  mb: 1,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                startIcon={<HomeIcon />}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                Inicio
              </Button>
              <Button
                fullWidth
                sx={{
                  justifyContent: 'flex-start',
                  color: scrolled ? '#602442' : 'white',
                  fontWeight: 'bold',
                  py: 1.5,
                  mb: 1,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                startIcon={<HospitalIcon />}
                onClick={() => {
                  navigate('/servicios');
                  window.scrollTo(0, 0);
                  setMobileMenuOpen(false);
                }}
              >
                Servicios
              </Button>
              <Button
                fullWidth
                sx={{
                  justifyContent: 'flex-start',
                  color: scrolled ? '#602442' : 'white',
                  fontWeight: 'bold',
                  py: 1.5,
                  mb: 1,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                startIcon={<InfoIcon />}
                onClick={() => {
                  navigate('/nosotros');
                  window.scrollTo(0, 0);
                  setMobileMenuOpen(false);
                }}
              >
                Nosotros
              </Button>
              <Button
                fullWidth
                sx={{
                  justifyContent: 'flex-start',
                  color: scrolled ? '#602442' : 'white',
                  fontWeight: 'bold',
                  py: 1.5,
                  mb: 1,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                startIcon={<PetshopIcon />}
                onClick={() => {
                  navigate('/petshop');
                  window.scrollTo(0, 0);
                  setMobileMenuOpen(false);
                }}
              >
                Petshop
              </Button>
              <Button
                fullWidth
                sx={{
                  justifyContent: 'flex-start',
                  color: scrolled ? '#602442' : 'white',
                  fontWeight: 'bold',
                  py: 1.5,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                startIcon={<ContactIcon />}
                onClick={() => {
                  navigate('/contactos');
                  window.scrollTo(0, 0);
                  setMobileMenuOpen(false);
                }}
              >
                Contacto
              </Button>
            </Box>
          </Box>
        </Collapse>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: 'linear-gradient(rgba(196, 100, 148, 0.7), rgba(96, 36, 66, 0.7)), url("/inicio.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          color: 'white',
          textAlign: 'center',
          minHeight: { xs: '90vh', sm: '95vh', md: '100vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          pt: { xs: '60px', sm: '70px', md: '80px' },
          px: { xs: 2, sm: 3, md: 0 }
        }}
      >
        <Container maxWidth="md" sx={{ zIndex: 2, px: { xs: 1, sm: 2, md: 3 } }}>
          <Typography 
            variant="h1" 
            component="h1" 
            gutterBottom 
            fontWeight="bold"
            sx={{ 
              fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem', lg: '4.5rem' },
              textShadow: '3px 3px 6px rgba(0,0,0,0.8)',
              mb: { xs: 2, sm: 3 },
              lineHeight: { xs: 1.2, sm: 1.3 }
            }}
          >
            Cuidamos a tu mejor amigo
          </Typography>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: { xs: 4, sm: 5, md: 6 }, 
              opacity: 0.95,
              fontSize: { xs: '1rem', sm: '1.3rem', md: '1.6rem', lg: '2rem' },
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
              fontWeight: 400,
              lineHeight: { xs: 1.3, sm: 1.4 },
              px: { xs: 1, sm: 0 }
            }}
          >
            Atención veterinaria profesional a domicilio en Popayán con amor y dedicación
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 2, sm: 3 }, 
            justifyContent: 'center', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            px: { xs: 2, sm: 0 }
          }}>
            <Button
              variant="contained"
              size="large"
              sx={{ 
                backgroundColor: '#a55e88', 
                '&:hover': { 
                  backgroundColor: '#602442',
                  transform: 'scale(1.05)'
                },
                px: { xs: 4, sm: 5, md: 6 },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                fontWeight: 'bold',
                borderRadius: 3,
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                transition: 'all 0.3s ease',
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: '300px', sm: 'none' }
              }}
              startIcon={<WhatsAppIcon />}
              href="https://wa.me/573216103829?text=Hola,%20me%20gustaría%20agendar%20una%20cita"
              target="_blank"
            >
              Agendar por WhatsApp
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  transform: 'scale(1.05)'
                },
                px: { xs: 4, sm: 5, md: 6 },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                fontWeight: 'bold',
                borderRadius: 3,
                borderWidth: 2,
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                transition: 'all 0.3s ease',
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: '300px', sm: 'none' }
              }}
              startIcon={<PhoneIcon />}
              href="tel:3216103829"
            >
              Llamar Ahora
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Servicios */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 }, px: { xs: 2, sm: 3 } }} id="servicios">
        <Typography 
          variant="h3" 
          align="center" 
          gutterBottom 
          sx={{ 
            color: '#602442',
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
          }} 
          fontWeight="bold"
        >
          Nuestros Servicios
        </Typography>
        <Typography 
          variant="h6" 
          align="center" 
          sx={{ 
            color: '#a55e88', 
            mb: { xs: 4, sm: 6 },
            fontSize: { xs: '1rem', sm: '1.2rem' },
            px: { xs: 2, sm: 0 }
          }}
        >
          Ofrecemos atención a domicilio para el bienestar de tu mascota
        </Typography>
        
        {isMobile ? (
          <Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                pb: 2,
                px: 0.5,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {servicios.map((servicio, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: '0 0 82%',
                    maxWidth: '82%',
                    scrollSnapAlign: 'center',
                    display: 'flex'
                  }}
                >
                  {renderServicioCard(servicio)}
                </Box>
              ))}
            </Box>
            <Typography
              variant="caption"
              align="center"
              sx={{ display: 'block', mt: 1, color: '#a55e88' }}
            >
              Desliza para ver todos los servicios
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 3, sm: 4 }} justifyContent="center">
            {servicios.map((servicio, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                {renderServicioCard(servicio)}
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Por qué elegirnos */}
      <Box sx={{ backgroundColor: '#fcdcf4', py: 8 }} id="nosotros">
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ color: '#602442' }} fontWeight="bold">
            ¿Por qué elegirnos?
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2, justifyContent: 'center' }}>
            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Paper sx={{ p: 4, textAlign: 'center', height: '100%', width: '100%', maxWidth: '350px', backgroundColor: 'white', boxShadow: 3 }}>
                <MedicalIcon fontSize="large" sx={{ color: '#c46494', mb: 2 }} />
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#602442' }}>
                  Profesional Certificado
                </Typography>
                <Typography sx={{ color: '#602442' }}>
                  Veterinario con años de experiencia y certificaciones actualizadas.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Paper sx={{ p: 4, textAlign: 'center', height: '100%', width: '100%', maxWidth: '350px', backgroundColor: 'white', boxShadow: 3 }}>
                <PetsIcon fontSize="large" sx={{ color: '#c46494', mb: 2 }} />
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#602442' }}>
                  Amor por los Animales
                </Typography>
                <Typography sx={{ color: '#602442' }}>
                  Tratamos a cada mascota con el amor y cuidado que merece.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonios */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 }, position: 'relative' }}>
          {/* Decorative background */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: 200, sm: 300 },
              height: { xs: 100, sm: 150 },
              background: 'radial-gradient(ellipse, rgba(196, 100, 148, 0.1) 0%, transparent 70%)',
              borderRadius: '50%',
              zIndex: 0
            }}
          />
          
          <Typography 
            variant="h3" 
            sx={{ 
              background: 'linear-gradient(45deg, #602442 30%, #c46494 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.5rem' },
              fontWeight: 'bold',
              position: 'relative',
              zIndex: 1,
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                width: 100,
                height: 4,
                background: 'linear-gradient(90deg, #c46494, #a55e88, #c46494)',
                transform: 'translateX(-50%)',
                borderRadius: 2
              }
            }}
          >
          Lo que dicen nuestros clientes
          </Typography>
          
          <Typography
            variant="subtitle1"
            sx={{
              color: '#a55e88',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontStyle: 'italic',
              mt: 2,
              opacity: 0.9,
              position: 'relative',
              zIndex: 1
            }}
          >
            Testimonios reales de familias felices
          </Typography>
        </Box>
        
        {/* Carrusel de testimonios */}
        <Box sx={{ position: 'relative', mt: { xs: 3, sm: 4 } }}>
          <Box 
            sx={{ 
              overflow: 'hidden',
              width: '100%',
              position: 'relative',
              mx: 'auto',
              maxWidth: '900px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                transition: 'transform 0.5s ease-in-out',
                transform: `translateX(-${currentTestimonio * (100 / Math.ceil(testimonios.length / (isMobile ? 1 : 2)))}%)`,
                width: `${Math.ceil(testimonios.length / (isMobile ? 1 : 2)) * 100}%`
              }}
            >
              {Array.from({ length: Math.ceil(testimonios.length / (isMobile ? 1 : 2)) }, (_, groupIndex) => (
                <Box
                  key={groupIndex}
                  sx={{
                    width: `${100 / Math.ceil(testimonios.length / (isMobile ? 1 : 2))}%`,
                    display: 'flex',
                    gap: { xs: 0, md: 3 },
                    px: { xs: 1, md: 2 },
                    flexShrink: 0
                  }}
                >
                  {testimonios.slice(groupIndex * (isMobile ? 1 : 2), groupIndex * (isMobile ? 1 : 2) + (isMobile ? 1 : 2)).map((testimonio, index) => (
                    <Box
                      key={groupIndex * (isMobile ? 1 : 2) + index}
                      sx={{
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      <Card 
                        sx={{ 
                          height: { xs: '320px', sm: '360px', md: '380px' }, 
                          p: { xs: 2, sm: 2.5, md: 3 },
                          background: 'linear-gradient(135deg, #fcdcf4 0%, #f8c8e8 50%, #f4b4dc 100%)',
                          boxShadow: '0 8px 32px rgba(196, 100, 148, 0.2)',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          borderRadius: 4,
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          position: 'relative',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: 'translateY(0)',
                          '&:hover': {
                            transform: 'translateY(-8px) scale(1.02)',
                            boxShadow: '0 20px 40px rgba(196, 100, 148, 0.3)',
                            background: 'linear-gradient(135deg, #fce8f6 0%, #f9d4ea 50%, #f6c0de 100%)',
                            '& .MuiTypography-root:last-child::before': {
                              width: '60%'
                            }
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                            transition: 'left 0.8s ease-in-out',
                          },
                          '&:hover::before': {
                            left: '100%',
                          }
                        }}
                      >
                        {/* Decorative elements */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -20,
                            right: -20,
                            width: 60,
                            height: 60,
                            background: 'linear-gradient(45deg, #c46494, #a55e88)',
                            borderRadius: '50%',
                            opacity: 0.1,
                            animation: 'float 3s ease-in-out infinite',
                            '@keyframes float': {
                              '0%, 100%': { transform: 'translateY(0px)' },
                              '50%': { transform: 'translateY(-10px)' }
                            }
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: -15,
                            left: -15,
                            width: 40,
                            height: 40,
                            background: 'linear-gradient(45deg, #a55e88, #8a4a73)',
                            borderRadius: '50%',
                            opacity: 0.1,
                            animation: 'float 3s ease-in-out infinite 1.5s',
                          }}
                        />
                        
                        <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
                            <Box
                              sx={{
                                position: 'relative',
                                mr: { xs: 1.5, md: 2 }
                              }}
                            >
                              <Avatar 
                                sx={{ 
                                  bgcolor: 'transparent',
                                  background: 'linear-gradient(135deg, #c46494 0%, #a55e88 100%)',
                                  width: { xs: 50, sm: 60, md: 70 }, 
                                  height: { xs: 50, sm: 60, md: 70 },
                                  boxShadow: '0 4px 15px rgba(196, 100, 148, 0.3)',
                                  border: '3px solid rgba(255, 255, 255, 0.5)',
                                  transition: 'all 0.3s ease',
                                  animation: 'pulse 2s ease-in-out infinite',
                                  '@keyframes pulse': {
                                    '0%, 100%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.05)' }
                                  }
                                }}
                              >
                                <Typography variant="h4" sx={{ 
                                  fontWeight: 'bold', 
                                  color: 'white',
                                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' }
                                }}>
                                  {testimonio.nombre[0]}
                                </Typography>
                              </Avatar>
                              {/* Sparkle effect */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -5,
                                  right: -5,
                                  width: 20,
                                  height: 20,
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    width: '100%',
                                    height: '2px',
                                    background: '#fff',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%) rotate(0deg)',
                                    borderRadius: '1px',
                                  },
                                  '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    width: '100%',
                                    height: '2px',
                                    background: '#fff',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%) rotate(90deg)',
                                    borderRadius: '1px',
                                  },
                                  animation: 'sparkle 2s ease-in-out infinite',
                                  '@keyframes sparkle': {
                                    '0%, 100%': { opacity: 0, transform: 'scale(0)' },
                                    '50%': { opacity: 1, transform: 'scale(1)' }
                                  }
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: '#602442', 
                                  fontWeight: 'bold', 
                                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                  background: 'linear-gradient(45deg, #602442, #8a4a73)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  backgroundClip: 'text'
                                }}
                              >
                                {testimonio.nombre}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#a55e88', 
                                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.95rem' },
                                  fontWeight: 500,
                                  opacity: 0.9
                                }}
                              >
                                🐾 {testimonio.mascota}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Rating 
                            value={testimonio.rating} 
                            readOnly 
                            sx={{ 
                              mb: { xs: 2, md: 3 }, 
                              '& .MuiRating-iconFilled': { 
                                color: '#c46494',
                                filter: 'drop-shadow(0 2px 4px rgba(196, 100, 148, 0.3))',
                                animation: 'starGlow 2s ease-in-out infinite alternate'
                              },
                              '& .MuiRating-icon': { 
                                fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
                                transition: 'all 0.3s ease'
                              },
                              '&:hover .MuiRating-icon': {
                                transform: 'scale(1.1)'
                              },
                              '@keyframes starGlow': {
                                '0%': { filter: 'drop-shadow(0 2px 4px rgba(196, 100, 148, 0.3))' },
                                '100%': { filter: 'drop-shadow(0 4px 8px rgba(196, 100, 148, 0.6))' }
                              }
                            }} 
                          />
                          
                          <Box 
                            sx={{ 
                              flex: 1, 
                              display: 'flex', 
                              alignItems: 'center',
                              position: 'relative'
                            }}
                          >
                            {/* Quote decoration */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -10,
                                left: -5,
                                fontSize: '3rem',
                                color: '#c46494',
                                opacity: 0.2,
                                fontFamily: 'serif',
                                fontWeight: 'bold',
                                lineHeight: 1,
                                pointerEvents: 'none'
                              }}
                            >
                              "
                            </Box>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontStyle: 'italic',
                                color: '#602442',
                                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.05rem' },
                                lineHeight: 1.6,
                                textAlign: 'center',
                                fontWeight: 500,
                                textShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                position: 'relative',
                                zIndex: 1,
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: -5,
                                  left: '50%',
                                  width: 0,
                                  height: 2,
                                  background: 'linear-gradient(90deg, #c46494, #a55e88)',
                                  transform: 'translateX(-50%)',
                                  transition: 'width 0.3s ease',
                                  borderRadius: '1px'
                                }
                              }}
                            >
                              {testimonio.testimonio}
                            </Typography>
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: -10,
                                right: -5,
                                fontSize: '3rem',
                                color: '#c46494',
                                opacity: 0.2,
                                fontFamily: 'serif',
                                fontWeight: 'bold',
                                lineHeight: 1,
                                pointerEvents: 'none',
                                transform: 'rotate(180deg)'
                              }}
                            >
                              "
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Botones de navegación */}
          <IconButton
            onClick={prevTestimonio}
            sx={{
              position: 'absolute',
              left: { xs: -15, md: -30 },
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'linear-gradient(135deg, #c46494 0%, #a55e88 100%)',
              color: 'white',
              width: 60,
              height: 60,
              boxShadow: '0 8px 25px rgba(196, 100, 148, 0.4)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 10,
              '&:hover': { 
                background: 'linear-gradient(135deg, #a55e88 0%, #8a4a73 100%)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 12px 35px rgba(196, 100, 148, 0.6)',
              },
              '&:active': {
                transform: 'translateY(-50%) scale(0.95)'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #c46494, #a55e88, #c46494)',
                zIndex: -1,
                animation: 'rotate 3s linear infinite',
                '@keyframes rotate': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: '1.5rem' }} />
          </IconButton>
          
          <IconButton
            onClick={nextTestimonio}
            sx={{
              position: 'absolute',
              right: { xs: -15, md: -30 },
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'linear-gradient(135deg, #c46494 0%, #a55e88 100%)',
              color: 'white',
              width: 60,
              height: 60,
              boxShadow: '0 8px 25px rgba(196, 100, 148, 0.4)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 10,
              '&:hover': { 
                background: 'linear-gradient(135deg, #a55e88 0%, #8a4a73 100%)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 12px 35px rgba(196, 100, 148, 0.6)',
              },
              '&:active': {
                transform: 'translateY(-50%) scale(0.95)'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #c46494, #a55e88, #c46494)',
                zIndex: -1,
                animation: 'rotate 3s linear infinite reverse',
              }
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: '1.5rem' }} />
          </IconButton>

          {/* Indicadores de puntos */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5, gap: 1 }}>
            {Array.from({ length: Math.ceil(testimonios.length / 2) }, (_, index) => (
              <Box
                key={index}
                onClick={() => goToTestimonio(index)}
                sx={{
                  width: index === currentTestimonio ? 24 : 12,
                  height: 12,
                  borderRadius: 6,
                  background: index === currentTestimonio 
                    ? 'linear-gradient(90deg, #c46494, #a55e88)' 
                    : '#d1c4e9',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: index === currentTestimonio 
                    ? '0 4px 15px rgba(196, 100, 148, 0.4)' 
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  '&:hover': { 
                    background: 'linear-gradient(90deg, #a55e88, #8a4a73)',
                    transform: 'scale(1.2)',
                    boxShadow: '0 6px 20px rgba(196, 100, 148, 0.6)'
                  },
                  '&::after': index === currentTestimonio ? {
                    content: '""',
                    position: 'absolute',
                    inset: -3,
                    borderRadius: 6,
                    background: 'linear-gradient(45deg, #c46494, #a55e88)',
                    opacity: 0.3,
                    animation: 'pulse-ring 2s ease-in-out infinite',
                    '@keyframes pulse-ring': {
                      '0%': { transform: 'scale(1)', opacity: 0.3 },
                      '50%': { transform: 'scale(1.2)', opacity: 0.1 },
                      '100%': { transform: 'scale(1)', opacity: 0.3 }
                    }
                  } : {}
                }}
              />
            ))}
          </Box>
        </Box>
      </Container>

      {/* Horarios */}
      <Box sx={{ backgroundColor: '#c9aebc', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" align="center" gutterBottom sx={{ color: '#602442' }} fontWeight="bold">
            Horarios de Atención
          </Typography>
          <Paper sx={{ p: 4, mt: 4, backgroundColor: 'white' }}>
            {horarios.map((horario, index) => (
              <Box 
                key={index}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  py: 2,
                  borderBottom: index < horarios.length - 1 ? '1px solid #c9aebc' : 'none'
                }}
              >
                <Typography variant="h6" sx={{ color: '#602442' }}>{horario.dia}</Typography>
                <Typography variant="h6" sx={{ color: '#c46494' }}>{horario.horario}</Typography>
              </Box>
            ))}
          </Paper>
        </Container>
      </Box>

      {/* Información de contacto */}
      <Container maxWidth="lg" sx={{ py: 8 }} id="contacto">
        <Typography variant="h3" align="center" gutterBottom sx={{ color: '#602442' }} fontWeight="bold">
          Contáctanos
        </Typography>
        <Typography variant="h6" align="center" sx={{ color: '#a55e88', mb: 6 }}>
          Estamos aquí para atender a tu mascota cuando más lo necesite
        </Typography>
        
        <Grid container spacing={4} sx={{ mt: 2 }} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              component="a"
              href="https://www.instagram.com/belkys.veterinaria/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                backgroundColor: 'white', 
                boxShadow: 4,
                borderRadius: 3,
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': { 
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                },
                border: '2px solid #fcdcf4',
                display: 'block',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
            >
              <Box 
                sx={{ 
                  backgroundColor: '#fcdcf4', 
                  borderRadius: '50%', 
                  width: 80, 
                  height: 80, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <InstagramIcon fontSize="large" sx={{ color: '#c46494' }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#602442', fontWeight: 'bold' }}>
                Instagram
              </Typography>
              <Typography variant="h6" sx={{ color: '#a55e88', mb: 2 }}>
                belkys.veterinaria
              </Typography>
              <Typography variant="body2" sx={{ color: '#602442' }}>
                Síguenos y escríbenos por mensaje directo
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              component="a"
              href="mailto:belkys.veterinaria@gmail.com"
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                backgroundColor: 'white', 
                boxShadow: 4,
                borderRadius: 3,
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': { 
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                },
                border: '2px solid #fcdcf4',
                display: 'block',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
            >
              <Box 
                sx={{ 
                  backgroundColor: '#fcdcf4', 
                  borderRadius: '50%', 
                  width: 80, 
                  height: 80, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <EmailIcon fontSize="large" sx={{ color: '#c46494' }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#602442', fontWeight: 'bold' }}>
                Email
              </Typography>
              <Typography variant="h6" sx={{ color: '#a55e88', mb: 2 }}>
                belkys.veterinaria@gmail.com
              </Typography>
              <Typography variant="body2" sx={{ color: '#602442' }}>
                Respuesta en menos de 24 horas
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              component="a"
              href="https://wa.me/573216103829?text=Hola,%20me%20gustaría%20agendar%20una%20cita"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                backgroundColor: 'white', 
                boxShadow: 4,
                borderRadius: 3,
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': { 
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                },
                border: '2px solid #fcdcf4',
                display: 'block',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
            >
              <Box 
                sx={{ 
                  backgroundColor: '#fcdcf4', 
                  borderRadius: '50%', 
                  width: 80, 
                  height: 80, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <WhatsAppIcon fontSize="large" sx={{ color: '#c46494' }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#602442', fontWeight: 'bold' }}>
                WhatsApp
              </Typography>
              <Typography variant="h6" sx={{ color: '#a55e88', mb: 2 }}>
                @belkys.veterinaria
              </Typography>
              <Typography variant="body2" sx={{ color: '#602442' }}>
                Respuesta inmediata
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Sección de Ubicación - Servicio a Domicilio */}
        <Box sx={{ mt: 8, mb: 8 }}>
          <Paper 
            elevation={3}
            sx={{ 
              p: { xs: 4, sm: 6 }, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #fcdcf4 0%, #f5c9e8 100%)',
              borderRadius: 4,
              border: '2px solid #c46494',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                background: 'radial-gradient(circle, rgba(196, 100, 148, 0.1) 0%, transparent 70%)',
                borderRadius: '50%'
              }
            }}
          >
            <Box 
              sx={{ 
                backgroundColor: '#602442', 
                borderRadius: '50%', 
                width: { xs: 80, sm: 100 }, 
                height: { xs: 80, sm: 100 }, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 8px 20px rgba(96, 36, 66, 0.3)'
              }}
            >
              <LocationIcon sx={{ fontSize: { xs: '2.5rem', sm: '3.5rem' }, color: '#fcdcf4' }} />
            </Box>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                color: '#602442', 
                fontWeight: 'bold',
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                mb: 2
              }}
            >
              🏠 Atención a Domicilio
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#a55e88', 
                mb: 3,
                fontSize: { xs: '1.1rem', sm: '1.4rem', md: '1.6rem' },
                fontWeight: 600
              }}
            >
              ¡Llevamos la veterinaria hasta tu hogar!
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: 1,
                mb: 2
              }}
            >
              <LocationIcon sx={{ color: '#602442', fontSize: '2rem' }} />
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#602442', 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}
              >
                Popayán, Cauca
              </Typography>
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#602442', 
                maxWidth: 700, 
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.2rem' },
                lineHeight: 1.6
              }}
            >
              Brindamos servicios veterinarios profesionales en toda la ciudad de Popayán. 
              Tu mascota recibirá atención de calidad sin salir de casa, reduciendo su estrés 
              y brindándole mayor comodidad.
            </Typography>
            <Box sx={{ mt: 4 }}>
              <Chip 
                label="Cobertura en toda la ciudad" 
                sx={{ 
                  backgroundColor: '#602442', 
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  py: { xs: 2, sm: 3 },
                  px: { xs: 1, sm: 2 },
                  height: 'auto'
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* Sección de llamada a la acción */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#602442', fontWeight: 'bold' }}>
            ¿Listo para cuidar a tu mascota?
          </Typography>
          <Typography variant="h6" sx={{ color: '#a55e88', mb: 4 }}>
            Contáctanos ahora y agenda tu cita
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{ 
                backgroundColor: '#c46494', 
                '&:hover': { backgroundColor: '#a55e88' },
                px: 4,
                py: 2,
                borderRadius: 3,
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
              startIcon={<WhatsAppIcon />}
              href="https://wa.me/573216103829?text=Hola,%20me%20gustaría%20agendar%20una%20cita"
              target="_blank"
            >
              Agendar por WhatsApp
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ 
                borderColor: '#c46494', 
                color: '#c46494',
                '&:hover': { 
                  backgroundColor: '#c46494',
                  color: 'white'
                },
                px: 4,
                py: 2,
                borderRadius: 3,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderWidth: 2
              }}
              startIcon={<PhoneIcon />}
              href="tel:3216103829"
            >
              Llamar Ahora
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ backgroundColor: '#602442', color: 'white', py: { xs: 3, sm: 4 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 3, sm: 4 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: { xs: 1, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <img 
                  src="/logo192.png" 
                  alt="Belkysvet Logo" 
                  style={{ 
                    height: window.innerWidth < 600 ? '60px' : '80px', 
                    width: 'auto',
                    marginRight: window.innerWidth < 600 ? '0' : '20px',
                    marginBottom: window.innerWidth < 600 ? '10px' : '0'
                  }} 
                />
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center',
                  alignItems: { xs: 'center', sm: 'flex-start' }
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#c46494', 
                      mb: 1, 
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    Belkysveterinaria
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 0, 
                      lineHeight: 1.4,
                      fontSize: { xs: '0.85rem', sm: '0.875rem' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}
                  >
                    Atención veterinaria profesional en la comodidad de tu hogar
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mt: 1.5,
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}>
                    <LocationIcon sx={{ 
                      fontSize: { xs: '1rem', sm: '1.1rem' }, 
                      color: '#c46494',
                      mr: 0.5 
                    }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.85rem', sm: '0.875rem' },
                        color: '#fcdcf4',
                        fontWeight: '500'
                      }}
                    >
                      Servicio a domicilio - Popayán, Cauca
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: { xs: 'center', md: 'flex-end' },
                justifyContent: 'center',
                height: '100%',
                textAlign: { xs: 'center', md: 'right' }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1,
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                  <IconButton
                    href="https://wa.me/573216103829?text=Hola,%20me%20gustaría%20agendar%20una%20cita"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#c46494',
                      '&:hover': {
                        color: '#fcdcf4',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease',
                      p: 1
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }} />
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' }, 
                      fontWeight: '500',
                      ml: 1
                    }}
                  >
                    @belkys.veterinaria
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'flex-end' },
                  mb: 1
                }}>
                  <IconButton
                    href="mailto:belkys.veterinaria@gmail.com"
                    sx={{
                      color: '#c46494',
                      '&:hover': {
                        color: '#fcdcf4',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease',
                      p: 1
                    }}
                  >
                    <EmailIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }} />
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' }, 
                      fontWeight: '500',
                      ml: 1
                    }}
                  >
                    belkys.veterinaria@gmail.com
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                  <IconButton
                    href="https://www.instagram.com/belkys.veterinaria/"
                    target="_blank"
                    sx={{
                      color: '#c46494',
                      '&:hover': {
                        color: '#fcdcf4',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease',
                      p: 1
                    }}
                  >
                    <InstagramIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem' } }} />
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' }, 
                      fontWeight: '500',
                      ml: 1
                    }}
                  >
                    @belkys.veterinaria
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ 
            textAlign: 'center', 
            mt: { xs: 2, sm: 3 }, 
            pt: { xs: 2, sm: 3 }, 
            borderTop: '1px solid rgba(255,255,255,0.2)' 
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.8,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              © 2025 Belkysveterinaria. Todos los derechos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Botón flotante de WhatsApp */}
      <Fab
        color="success"
        aria-label="whatsapp"
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 20 },
          right: { xs: 16, sm: 20 },
          backgroundColor: '#c46494',
          '&:hover': { backgroundColor: '#a55e88' },
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          zIndex: 1000
        }}
        href="https://wa.me/573216103829?text=Hola,%20me%20gustaría%20agendar%20una%20cita"
        target="_blank"
      >
        <WhatsAppIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
      </Fab>
    </Box>
  );
};

export default DashboardInicio;